import React, { useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { usePaymentSettings } from "../hooks/usePaymentSettings";
import { useParamPaymentSettings } from "../hooks/useParamPaymentSettings";
import { useEsnekPosSettings } from "../hooks/useEsnekPosSettings";
import { useQnbPaymentSettings } from "../hooks/useQnbPaymentSettings";
import { useGarantiPaymentSettings } from "../hooks/useGarantiPaymentSettings";
import { useIsyerimPosSettings } from "../hooks/useIsyerimPosSettings";
import ActivePosSection from "./ActivePosSection";

import { CATEGORIES, integrationsByCategory } from "./integrations/registry";
import CategoryTabs from "./integrations/CategoryTabs";
import IntegrationSearch from "./integrations/IntegrationSearch";
import IntegrationCard from "./integrations/IntegrationCard";
import IntegrationConfigModal from "./integrations/IntegrationConfigModal";
import PaymentIntegrationFields, { PAY_FIELD_STYLES } from "./integrations/PaymentIntegrationFields";
import IsyerimPosApplicationForm from "./integrations/IsyerimPosApplicationForm";
import IntegrationStatusPanel from "./integrations/IntegrationStatusPanel";
import { PAYMENT_FIELD_SCHEMAS } from "./integrations/paymentFieldSchemas";

import { usePlanIntegrations } from "../../../hooks/usePlanIntegrations";
import { getActivePosOrphanSummary, migrateAllBrandsToVirtualPos } from "../../../api/requests/company-requests";
// Bu sekmede gösterilen kategoriler: Ödeme Kuruluşu + Bankalar
const TAB_CATEGORIES = CATEGORIES.filter((c) => c.id === "payment" || c.id === "bank");

// Panel sağlayıcı id'si → backend VirtualPos enum'u. Bağlantı kaldırıldıktan sonra
// "markaları aktaralım mı?" sorusunda hangi POS'un boşaldığını bilmek için gerekir.
const PROVIDER_LABELS = {
    PAYTR: "PayTR",
    PARAMPOS: "ParamPOS",
    ESNEK_POS: "Hoppa (EsnekPos)",
    QNB_PAYFOR: "QNB PayFor",
    GARANTI: "Garanti BBVA",
    ISYERIM_POS: "İşyerimPOS",
};

const PROVIDER_TO_VIRTUAL_POS = {
    paytr: "PAYTR",
    param: "PARAMPOS",
    esnekpos: "ESNEK_POS",
    qnb_payfor: "QNB_PAYFOR",
    garanti: "GARANTI",
    isyerimpos: "ISYERIM_POS",
};

const trIncludes = (text, term) =>
    (text || "").toLocaleLowerCase("tr-TR").includes((term || "").toLocaleLowerCase("tr-TR"));

export default function PaymentSettingsTab({ companyId, companyCode }) {
    const { has: hasIntegration } = usePlanIntegrations();
    const { formik: paytrFormik, loading: paytrLoading } = usePaymentSettings(companyId, companyCode);
    const { formik: paramFormik, loading: paramLoading, hasSettings: hasParam, disconnect: paramDisconnect } = useParamPaymentSettings(companyId);
    const { formik: esnekFormik, loading: esnekLoading, hasSettings: hasEsnek, disconnect: esnekDisconnect } = useEsnekPosSettings(companyId);
    const { formik: qnbFormik, loading: qnbLoading, hasSettings: hasQnb, disconnect: qnbDisconnect } = useQnbPaymentSettings(companyId);
    const { formik: garantiFormik, loading: garantiLoading, hasSettings: hasGaranti, disconnect: garantiDisconnect } = useGarantiPaymentSettings(companyId);
    const { formik: isyerimFormik, loading: isyerimLoading, hasSettings: hasIsyerim, disconnect: isyerimDisconnect } = useIsyerimPosSettings(companyId);

    const [activeCategory, setActiveCategory] = useState("payment");
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    // Bağla penceresi üye iş yeri başvuru formuna dönmüş mü
    const [applicationOpen, setApplicationOpen] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    // Bağlantı kaldırıldıktan sonra o POS'ta kalan kart markalarının devri
    const [transferPrompt, setTransferPrompt] = useState(null);
    const [transferTarget, setTransferTarget] = useState("");
    const [transferring, setTransferring] = useState(false);

    // Sağlayıcı id → formik/loading/bağlı durumu
    const providerCtx = useMemo(
        () => ({
            paytr: { formik: paytrFormik, loading: paytrLoading, connected: !!paytrFormik.values.merchantId },
            param: { formik: paramFormik, loading: paramLoading, connected: hasParam, disconnect: paramDisconnect },
            esnekpos: { formik: esnekFormik, loading: esnekLoading, connected: hasEsnek, disconnect: esnekDisconnect },
            qnb_payfor: { formik: qnbFormik, loading: qnbLoading, connected: hasQnb, disconnect: qnbDisconnect },
            garanti: { formik: garantiFormik, loading: garantiLoading, connected: hasGaranti, disconnect: garantiDisconnect },
            isyerimpos: { formik: isyerimFormik, loading: isyerimLoading, connected: hasIsyerim, disconnect: isyerimDisconnect },
        }),
        [paytrFormik, paytrLoading, paramFormik, paramLoading, hasParam, paramDisconnect, esnekFormik, esnekLoading, hasEsnek, esnekDisconnect, qnbFormik, qnbLoading, hasQnb, qnbDisconnect, garantiFormik, garantiLoading, hasGaranti, garantiDisconnect, isyerimFormik, isyerimLoading, hasIsyerim, isyerimDisconnect]
    );

    // Sanal POS bağlandıktan sonra aşağıdaki "Aktif POS" bölümü kendini tazelesin.
    // O bölüm verisini yalnız mount'ta çekiyordu; yeni bağlanan POS "Aktif POS ekle"
    // listesine ancak sayfa yenilenince düşüyordu. Yapılandırma modalı her
    // kapandığında anahtarı artırıyoruz — bağlama, kaldırma ve PayTR gibi
    // "bağlı mı" bilgisi form değerinden türeyen sağlayıcılar tek yerden kapsanır.
    // (Bağlantı kaydı backend'de aktif POS satırlarını aynı istek içinde
    // oluşturuyor, yani modal kapandığında veri hazır.)
    const [activePosRefreshKey, setActivePosRefreshKey] = useState(0);

    // Bir sağlayıcının kart durumu
    const statusOf = (integration) => {
        if (integration.status === "soon") return "soon";
        // Paket kapsamı dışındaki sağlayıcı kilitli çizilir. Kaydetmeyi backend
        // zaten 403 ile engelliyordu; burada amaç kullanıcıya kaydedemeyeceği
        // formu hiç doldurtmamak.
        if (!hasIntegration(integration.planIntegration)) return "locked";
        const ctx = providerCtx[integration.id];
        if (!ctx) return "soon"; // henüz desteklenmeyen
        return ctx.connected ? "connected" : "passive";
    };

    const tabs = TAB_CATEGORIES.map((c) => ({
        id: c.id,
        label: c.label,
        count: integrationsByCategory(c.id).length,
    }));

    const cards = integrationsByCategory(activeCategory).filter((i) => trIncludes(i.name, search));

    // Modal kaynağı: ödeme + banka kategorilerindeki (backend'i olan) sağlayıcılar
    const selected = selectedId
        ? [...integrationsByCategory("payment"), ...integrationsByCategory("bank")].find((i) => i.id === selectedId)
        : null;
    const selectedCtx = selectedId ? providerCtx[selectedId] : null;
    const showApplication = applicationOpen && Boolean(PAYMENT_FIELD_SCHEMAS[selectedId]?.signup?.form);
    const selectedConnected = selectedCtx?.connected;

    const closeModal = () => {
        setApplicationOpen(false);
        setSelectedId(null);
        setActivePosRefreshKey((k) => k + 1);
    };

    // Bağlantıyı kaldır. Sağlayıcı bir disconnect() sunuyorsa (backend DELETE endpoint'i
    // olanlar: Param, Hoppa, QNB, Garanti) kalıcı olarak siler. Sunmayanlar (ör. PayTR)
    // için yalnızca form alanlarını yerel olarak temizler.
    /**
     * Bağlantı kaldırıldıktan sonra o POS'a atanmış kart markaları varsa, kullanıcıya
     * hangi POS'a aktarılacağını sorar. Aktarılacak bağlı POS yoksa backend satırları
     * zaten temizlemiştir; burada soru da sorulmaz.
     */
    const askBrandTransfer = async (providerId, providerName) => {
        const vpos = PROVIDER_TO_VIRTUAL_POS[providerId];
        if (!vpos || !companyId) return;
        try {
            const res = await getActivePosOrphanSummary(companyId);
            const data = res?.data;
            const group = (data?.orphanGroups || []).find((g) => g.virtualPos === vpos);
            const targets = data?.transferTargets || [];
            if (!group || group.cardBrands?.length === 0 || targets.length === 0) return;

            setTransferTarget(targets.includes("PAYTR") ? "PAYTR" : targets[0]);
            setTransferPrompt({
                sourceVirtualPos: vpos,
                sourceName: providerName || vpos,
                brandCount: group.cardBrands.length,
                targets,
            });
        } catch {
            // Devir sorusu sorulamazsa bağlantı kaldırma yine de başarılıdır;
            // markalar Aktif POS ekranından elle taşınabilir.
        }
    };

    const doBrandTransfer = async () => {
        if (!transferPrompt || !transferTarget) return;
        setTransferring(true);
        try {
            const res = await migrateAllBrandsToVirtualPos(companyId, {
                sourceVirtualPos: transferPrompt.sourceVirtualPos,
                targetVirtualPos: transferTarget,
            });
            if (res?.statusCode === 200) {
                toast.success(res.message || "Kart markaları aktarıldı.");
                setActivePosRefreshKey((k) => k + 1);
            } else {
                toast.error(res?.message || "Kart markaları aktarılamadı.");
            }
        } catch {
            toast.error("Kart markaları aktarılamadı.");
        } finally {
            setTransferring(false);
            setTransferPrompt(null);
        }
    };

    const doDisconnect = async () => {
        const ctx = selectedCtx;

        if (ctx?.disconnect) {
            setDisconnecting(true);
            try {
                const ok = await ctx.disconnect();
                if (ok) {
                    toast.success(`${selected?.name} bağlantısı kaldırıldı.`);
                    setConfirmDisconnect(false);
                    closeModal();
                    await askBrandTransfer(selectedId, selected?.name);
                } else {
                    toast.error("Bağlantı kaldırılırken bir hata oluştu. Lütfen tekrar deneyin.");
                }
            } finally {
                setDisconnecting(false);
            }
            return;
        }

        // Disconnect endpoint'i olmayan sağlayıcılar → yerel temizleme.
        const f = ctx?.formik;
        if (f) {
            const cleared = Object.keys(f.values).reduce((acc, k) => {
                if (k === "companyId") acc[k] = f.values[k];
                else if (typeof f.values[k] === "boolean") acc[k] = false;
                else acc[k] = "";
                return acc;
            }, {});
            f.setValues(cleared);
        }
        toast.info("Bağlantı bilgileri temizlendi. Kalıcı olarak kaldırmak için Kaydet'e basın.");
        setConfirmDisconnect(false);
        closeModal();
    };

    return (
        <>
            <p className="text-muted mb-3">
                Firmanızın ödeme alabilmesi için anlaşmalı olduğunuz kuruluşu seçip yapılandırın.
            </p>

            {/* Kategori tabları + arama */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <CategoryTabs tabs={tabs} active={activeCategory} onChange={(id) => { setActiveCategory(id); setSearch(""); }} />
                <IntegrationSearch value={search} onChange={setSearch} />
            </div>

            {/* Kart grid */}
            {cards.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bx bx-search-alt fs-1 d-block mb-2" />
                    Aramanızla eşleşen entegrasyon bulunamadı.
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: "1rem",
                    }}
                >
                    {cards.map((integration) => (
                        <IntegrationCard
                            key={integration.id}
                            integration={integration}
                            status={statusOf(integration)}
                            onAction={() => statusOf(integration) === "locked" ? null : setSelectedId(integration.id)}
                        />
                    ))}
                </div>
            )}

            {/* Bağla / Yapılandır modal (yalnızca desteklenen ödeme sağlayıcıları) */}
            {selected && selectedCtx && (
                <IntegrationConfigModal
                    isOpen
                    toggle={closeModal}
                    integration={selected}
                    title={showApplication
                        ? PAYMENT_FIELD_SCHEMAS[selectedId].signup.label
                        : `${selected.name} ${selectedConnected ? "Yapılandır" : "Bağla"}`}
                    subtitle={showApplication
                        ? "Formu doldurun, başvurunuz satış ekibine iletilsin"
                        : selectedConnected ? "Entegrasyon ayarlarını düzenleyin" : "API anahtarlarınızı girerek bağlantı kurun"}
                    tabs={
                        selectedConnected
                            ? [
                                  { id: "genel", label: "Genel" },
                                  { id: "durum", label: "Durum" },
                                  { id: "gelismis", label: "Gelişmiş" },
                              ]
                            : undefined
                    }
                    // Başvuru formu kendi düğmelerini taşır; bağlantı düğmeleri gizlenir.
                    primaryLabel={showApplication ? undefined : selectedConnected ? "Kaydet" : "Bağlantıyı Kur"}
                    primaryIcon={selectedConnected ? "bx-save" : "bx-link"}
                    primaryLoading={selectedCtx.loading}
                    onPrimary={selectedCtx.formik.handleSubmit}
                    secondaryLabel={showApplication ? undefined : selectedConnected ? "Bağlantıyı Kaldır" : "İptal"}
                    secondaryVariant={selectedConnected ? "danger" : "light"}
                    onSecondary={selectedConnected ? () => setConfirmDisconnect(true) : closeModal}
                >
                    {(activeTab) => {
                        const schema = PAYMENT_FIELD_SCHEMAS[selectedId];
                        if (showApplication)
                            return (
                                <div className="pay-integration-fields">
                                    <style>{PAY_FIELD_STYLES}</style>
                                    <IsyerimPosApplicationForm
                                        companyId={companyId}
                                        onBack={() => setApplicationOpen(false)}
                                    />
                                </div>
                            );
                        if (activeTab === "durum")
                            return (
                                <IntegrationStatusPanel
                                    connected={selectedConnected}
                                    testMode={selectedCtx.formik.values.testMode}
                                />
                            );
                        if (activeTab === "gelismis")
                            return (
                                <PaymentIntegrationFields
                                    formik={selectedCtx.formik}
                                    schema={schema}
                                    showFields={false}
                                    showTestMode
                                    showInfo
                                />
                            );
                        // Genel (bağlı) / Bağla (pasif)
                        return (
                            <PaymentIntegrationFields
                                formik={selectedCtx.formik}
                                schema={schema}
                                onOpenApplication={() => setApplicationOpen(true)}
                                showHints={!selectedConnected}
                                showEnv={!selectedConnected}
                                // Üye iş yeri başvuru bağlantısı yalnızca henüz bağlı
                                // olmayan sağlayıcıda anlamlı; bağlıysa zaten üye iş yeri.
                                showSignup={!selectedConnected}
                            />
                        );
                    }}
                </IntegrationConfigModal>
            )}

            {/* Bağlantıyı kaldırma onayı */}
            <Modal isOpen={confirmDisconnect} toggle={() => setConfirmDisconnect(false)} centered>
                <ModalHeader toggle={() => setConfirmDisconnect(false)} className="text-danger">
                    <i className="bx bx-unlink me-1" /> Bağlantıyı Kaldır
                </ModalHeader>
                <ModalBody>
                    <p className="mb-0">
                        <strong>{selected?.name}</strong> bağlantısını kaldırmak istediğinize emin misiniz?{" "}
                        {selectedCtx?.disconnect
                            ? "Kayıtlı ayarlar silinecek, ancak girdiğiniz bilgiler formda kalacak (dilerseniz tekrar bağlayabilirsiniz)."
                            : "Girdiğiniz API bilgileri temizlenecektir."}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setConfirmDisconnect(false)} disabled={disconnecting}>
                        İptal
                    </Button>
                    <Button color="danger" onClick={doDisconnect} disabled={disconnecting}>
                        {disconnecting ? (
                            <><Spinner size="sm" className="me-1" /> Kaldırılıyor...</>
                        ) : (
                            <><i className="bx bx-unlink me-1" /> Evet, Kaldır</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Bağlantı kaldırıldı ama o POS'a atanmış kart markaları duruyor:
                ödeme yönlendirmesi ayarı olmayan bir POS'a gitmesin diye
                markaların nereye aktarılacağı sorulur. */}
            <Modal isOpen={!!transferPrompt} toggle={() => setTransferPrompt(null)} centered>
                <ModalHeader toggle={() => setTransferPrompt(null)}>
                    <i className="bx bx-transfer-alt me-1" /> Kart Markalarını Aktar
                </ModalHeader>
                <ModalBody>
                    <p>
                        <strong>{transferPrompt?.sourceName}</strong> bağlantısı kaldırıldı, ancak{" "}
                        <strong>{transferPrompt?.brandCount} kart markası</strong> hâlâ bu POS'a atanmış
                        durumda.
                    </p>
                    <label className="form-label small text-muted mb-1">Hangi POS'a aktarılsın?</label>
                    <select
                        className="form-select"
                        value={transferTarget}
                        onChange={(e) => setTransferTarget(e.target.value)}
                        disabled={transferring}
                    >
                        {(transferPrompt?.targets || []).map((t) => (
                            <option key={t} value={t}>
                                {PROVIDER_LABELS[t] || t}
                            </option>
                        ))}
                    </select>
                    <p className="text-muted small mt-2 mb-0">
                        Aktarmazsanız markaları daha sonra Aktif POS bölümünden tek tek taşıyabilirsiniz.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setTransferPrompt(null)} disabled={transferring}>
                        Şimdi Değil
                    </Button>
                    <Button color="primary" onClick={doBrandTransfer} disabled={transferring || !transferTarget}>
                        {transferring ? (
                            <><Spinner size="sm" className="me-1" /> Aktarılıyor...</>
                        ) : (
                            <><i className="bx bx-transfer-alt me-1" /> Aktar</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Aktif POS / kart markası → sanal POS eşleme (mevcut işlev korunur) */}
            <div className="mt-4 pt-2 border-top">
                <ActivePosSection companyId={companyId} refreshKey={activePosRefreshKey} />
            </div>
        </>
    );
}
